import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markPickupFulfilledFormSchema,VehicleFulfillmentStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Checkbox,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useGetMarkPickupFulfilled } from '../../../api/progress/useGetMarkPickupFulfilled';
import { useMarkPickupFulfilled } from '../../../api/progress/useMarkPickupFulfilled';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkPickupFulfilledDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkPickupFulfilledDetail component for marking a vehicle pickup as fulfilled.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkPickupFulfilledDetail = ({ actionItem }: MarkPickupFulfilledDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markPickupFulfilledMutation = useMarkPickupFulfilled(orderId);
	const { data: existingData, isLoading: isLoadingData } = useGetMarkPickupFulfilled(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markPickupFulfilledFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing data
	useEffect(() => {
		if (existingData?.vehicleFulfillmentStatus === VehicleFulfillmentStatus.FULFILLED) {
			reset({ isConfirmed: true });
		}
	}, [existingData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		try {
			await markPickupFulfilledMutation.mutateAsync();
			toast({
				message: 'Pickup marked as fulfilled successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to mark pickup as fulfilled';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm that the pickup has been completed and the vehicle has been picked up by the buyer. Note: an email will be sent to the buyer communicating the updated status.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingData ? (
					<SkeletonRect width='100%' height={48} />
				) : existingData?.vehicleFulfillmentStatus ===
				  VehicleFulfillmentStatus.FULFILLED ? (
					<Text size='body' color='success'>
						✓ Pickup already fulfilled
					</Text>
				) : null}

				{/* Show form if not complete */}
				{existingData?.vehicleFulfillmentStatus !== VehicleFulfillmentStatus.FULFILLED && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting ||
												markPickupFulfilledMutation.isPending
											}
											ariaLabel='Confirm pickup is fulfilled'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markPickupFulfilledMutation.isPending}
							isDisabled={isLoadingData}>
							{actionItem.config.buttonTitle || 'Mark pickup as fulfilled'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
